import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Native replacement for the `$rootScope.$broadcast`/`$on` channel between NATIVE
 * components (WS5 #10 Stage A inc 2i; full event census: ENDGAME-PLAN.md #106).
 * Delivery is synchronous (Subject.next, same call stack) — identical timing to an
 * in-zone `$rootScope.$broadcast`. `on()` returns a deregistration function (the
 * `$on` contract); the handler receives the PAYLOAD directly (no leading AngularJS
 * event object — call sites were adjusted with the move).
 *
 * Events that tell a list or journey page to refresh after an editor changed its data:
 *   `touchpoint.statusChanged`  a touchpoint was switched on/off (payload: the activate model)
 *   `campaign.statusChanged`    a campaign was switched on/off (payload: the activate model)
 *   `campaign.changed`          a campaign was created or edited (payload: the campaign)
 *   `flipto.photo-contest-changed | -activated | -closed`  a contest was edited, switched on/off, closed
 * Emit after the write has resolved. A page that shows that data listens for the event and
 * refetches, because closing an editor does not re-run the page underneath it.
 *
 * `[StoryModal].RecommendationComplete` also travels here (emitters = the native broadcast
 * flows + ft-recommendation-notification; listener = the native story modal).
 */
@Injectable({ providedIn: 'root' })
export class EventBusService {
  private readonly events$ = new Subject<{ name: string; payload: any }>();

  emit(name: string, payload?: any): void {
    this.events$.next({ name, payload });
  }

  /** Subscribe to a named event; returns the deregistration function (the `$on` contract). */
  on(name: string, handler: (payload: any) => void): () => void {
    const sub = this.events$.pipe(filter((e) => e.name === name)).subscribe((e) => handler(e.payload));
    return () => sub.unsubscribe();
  }
}
